import { OnboardingPersonSession } from "models/stateTypes/onboardingState";

export const ONBOARDING_SESSION_KEY_PREFIX = "onboarding_session";

/**
 * Stands in for the invite token in the generic flow, where there is none. Its company-session
 * slot and the OnboardingSession.inviteToken it matches against are then keyed exactly like an
 * invite's, so the wizard below the BankID step cannot tell the two flows apart.
 */
export const GENERIC_ONBOARDING_SCOPE = "person";

/**
 * Shares the company-session prefix on purpose: logging out of Wint in another tab clears storage
 * but preserves everything under it, and an onboarding visitor must not be signed out by that.
 */
export const ONBOARDING_PERSON_SESSION_KEY = `${ONBOARDING_SESSION_KEY_PREFIX}_person`;

/** Per-tab (sessionStorage): the company this tab is on, kept out of the shared person session. */
export const ONBOARDING_ACTIVE_COMPANY_KEY = `${ONBOARDING_SESSION_KEY_PREFIX}_active_company`;

/**
 * One storage slot per scope (an invite, or the generic flow), not one for the whole origin: two
 * onboarding tabs are two different scopes, and a shared slot means whichever tab minted its
 * session last silently overwrites the other's -- the other tab's very next request would then
 * carry the wrong company's token.
 */
export const getOnboardingSessionStorageKey = (scope: string) => `${ONBOARDING_SESSION_KEY_PREFIX}:${scope}`;

export const isUnexpired = (expiresAt: string, now: number = Date.now()) => new Date(expiresAt).getTime() > now;

/** The active company if it is still listed, otherwise the first the server ordered. */
export const targetCompanyId = (session: OnboardingPersonSession): string | null => {
  const active = session.companies.find((company) => company.companyId === session.activeCompanyId);
  return (active ?? session.companies[0])?.companyId ?? null;
};

export const withoutCompany = (session: OnboardingPersonSession, companyId: string): OnboardingPersonSession => ({
  ...session,
  companies: session.companies.filter((company) => company.companyId !== companyId),
  activeCompanyId: session.activeCompanyId === companyId ? null : session.activeCompanyId,
});

export const withActiveCompany = (session: OnboardingPersonSession, companyId: string): OnboardingPersonSession => ({
  ...session,
  activeCompanyId: companyId,
});

interface PersonSessionResponse {
  sessionToken: string;
  expiresAt: string;
  companies: OnboardingPersonSession["companies"];
}

export const toPersonSession = (
  response: PersonSessionResponse,
  identity: { givenName?: string; surname?: string } | undefined
): OnboardingPersonSession => ({
  token: response.sessionToken,
  expiresAt: response.expiresAt,
  companies: response.companies,
  activeCompanyId: null,
  givenName: identity?.givenName,
  surname: identity?.surname,
});

/** The rejection shape useAPIClientHelper's onError produces. */
interface ApiError {
  status?: number;
  codes?: string[];
}

const hasCode = (error: ApiError | undefined, ...codes: string[]) => !!error?.codes?.some((c) => codes.includes(c));

export const OnboardingPersonErrorCodes = {
  NoneOnboardable: "Company.NoneOnboardable",
  CompanyNotAllowed: "Session.CompanyNotAllowed",
  AccessBlocked: "Access.Blocked",
  NotOnboarding: "Company.NotOnboarding",
} as const;

export const isNoneOnboardable = (error: ApiError | undefined) =>
  error?.status === 409 && hasCode(error, OnboardingPersonErrorCodes.NoneOnboardable);

export type CompanySessionFailure = "personSessionInvalid" | "companyUnavailable" | "failed";

export const classifyCompanySessionError = (error: ApiError | undefined): CompanySessionFailure => {
  if (error?.status === 401) return "personSessionInvalid";
  const unavailable =
    (error?.status === 403 &&
      hasCode(error, OnboardingPersonErrorCodes.CompanyNotAllowed, OnboardingPersonErrorCodes.AccessBlocked)) ||
    (error?.status === 409 && hasCode(error, OnboardingPersonErrorCodes.NotOnboarding));
  return unavailable ? "companyUnavailable" : "failed";
};

const MINT_WINDOW_MS = 30_000;
const MAX_MINTS_IN_WINDOW = 3;

/**
 * Minting a company session again after a 401 is only sensible while it helps. A company session
 * that is refused as soon as it is minted would otherwise be replaced and refused in a loop.
 */
export const allowAutomaticMint = (mintTimes: number[], now: number) =>
  mintTimes.filter((time) => now - time < MINT_WINDOW_MS).length < MAX_MINTS_IN_WINDOW;

interface BankIdStatusShape {
  inProgress: boolean;
  session?: unknown;
  personSession?: unknown;
}

export type BankIdOutcome = "session" | "personSession" | "inProgress" | "failed";

/**
 * Each flow accepts only the session it asked for: the generic flow has no company session to
 * receive, and an invite poll that came back with a person session is not one it can use.
 */
export const interpretBankIdStatus = (status: BankIdStatusShape, generic: boolean): BankIdOutcome => {
  if (generic ? status.personSession : status.session) return generic ? "personSession" : "session";
  return status.inProgress ? "inProgress" : "failed";
};

export const pickValidPersonSession = (
  fromStore: OnboardingPersonSession | null,
  fromStorage: OnboardingPersonSession | null,
  now: number = Date.now()
): OnboardingPersonSession | null => {
  const session = fromStore ?? fromStorage;
  return session?.token && isUnexpired(session.expiresAt, now) ? session : null;
};
